import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { getRoute } from "../utils/routes";

const Navbar = ({ audience = "hiring", onContactClick }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLanguage = i18n.language || "tr";
  const [isScrolled, setIsScrolled] = useState(() => {
    // Check initial scroll position on mount
    if (typeof window !== "undefined") {
      return window.scrollY > 20;
    }
    return false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const isTalent = audience === "talent";

  useEffect(() => {
    // Check initial scroll position
    const checkScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    // Check on mount and location change
    // Use requestAnimationFrame to ensure DOM is ready after route change
    const rafId = requestAnimationFrame(() => {
      checkScroll();
    });

    const handleScroll = () => {
      checkScroll();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location]);

  const productLinks = isTalent
    ? [
        { name: t("hero.talentOffer1Title"), href: "#cv-analysis" },
        { name: t("hero.talentOffer2Title"), href: "#interview-prep" },
        { name: t("hero.talentOffer3Title"), href: "#role-growth" },
      ]
    : [
        {
          name: t("nav.cvScoring"),
          href: getRoute("cvScoring", currentLanguage),
          isRoute: true,
        },
        {
          name: t("nav.interviews"),
          href: "#interviews",
          hasDropdown: true,
          dropdownItems: [
            {
              name: t("nav_dropdown.premiumLiveAI"),
              href: getRoute("premiumInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.standardVideoAI"),
              href: getRoute("standardInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.voiceInterview"),
              href: getRoute("voiceInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.textInterview"),
              href: getRoute("textInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.whatsappInterview"),
              href: getRoute("whatsappInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.phoneInterview"),
              href: getRoute("phoneInterview", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav_dropdown.interviewObserver"),
              href: getRoute("interviewObserver", currentLanguage),
              isRoute: true,
            },
            {
              name: t("nav.peerInterview"),
              href: getRoute("peerInterview", currentLanguage),
              isRoute: true,
            },
          ],
        },
        {
          name: t("nav.assessments"),
          href: getRoute("assessments", currentLanguage),
          isRoute: true,
        },
        {
          name: t("nav.ats"),
          href: getRoute("ats", currentLanguage),
          isRoute: true,
        },
        {
          name: t("nav.training"),
          href: getRoute("training", currentLanguage),
          isRoute: true,
        },
      ];

  const NavLink = ({ link, isMobile = false }) => {
    const hasDropdown = link.hasDropdown;
    const isActive = activeDropdown === link.name;

    if (isMobile) {
      return (
        <div>
          {hasDropdown ? (
            <button
              type="button"
              className="text-lg sm:text-xl font-semibold text-white/90 hover:text-white transition-colors flex items-center gap-2 touch-manipulation py-2 bg-transparent border-0 w-full text-left"
              style={{ fontWeight: 600 }}
            >
              {link.name}
              <ChevronDown className="w-5 h-5" />
            </button>
          ) : link.isRoute ? (
            <Link
              to={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-lg sm:text-xl font-semibold text-white/90 hover:text-white transition-colors flex items-center gap-2 touch-manipulation py-2"
              style={{ fontWeight: 600 }}
            >
              {link.name}
            </Link>
          ) : (
            <a
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-lg sm:text-xl font-semibold text-white/90 hover:text-white transition-colors flex items-center gap-2 touch-manipulation py-2"
              style={{ fontWeight: 600 }}
            >
              {link.name}
            </a>
          )}
          {hasDropdown && (
            <div className="ml-4 mt-2 sm:mt-3 space-y-2 sm:space-y-3">
              {link.dropdownItems.map((item) =>
                item.isRoute ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-base sm:text-lg font-medium text-white/70 hover:text-white transition-colors touch-manipulation py-1.5"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-base sm:text-lg font-medium text-white/70 hover:text-white transition-colors touch-manipulation py-1.5"
                  >
                    {item.name}
                  </a>
                ),
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        className="relative"
        onMouseEnter={() => hasDropdown && setActiveDropdown(link.name)}
        onMouseLeave={() => setActiveDropdown(null)}
      >
        {hasDropdown ? (
          <button
            type="button"
            onClick={() => setActiveDropdown(isActive ? null : link.name)}
            className="group relative text-sm font-semibold transition-colors duration-300 flex items-center gap-1 py-2 text-gray-800 bg-transparent border-0 cursor-pointer"
            style={{ fontFamily: "var(--font-label)", fontWeight: 600 }}
          >
            {link.name}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 text-gray-800 ${isActive ? "rotate-180" : ""}`}
            />
            <span
              className={`absolute bottom-0 left-0 h-0.5 bg-accent-teal transition-all duration-150 ease-out ${
                isActive ? "w-full" : "w-0 group-hover:w-full"
              }`}
            />
          </button>
        ) : link.isRoute ? (
          <Link
            to={link.href}
            className="group relative text-sm font-semibold transition-colors duration-300 flex items-center gap-1 py-2 text-gray-800"
            style={{ fontFamily: "var(--font-label)", fontWeight: 600 }}
          >
            {link.name}
            <span className="absolute bottom-0 left-0 h-0.5 bg-accent-teal transition-all duration-150 ease-out w-0 group-hover:w-full" />
          </Link>
        ) : (
          <a
            href={link.href}
            className="group relative text-sm font-semibold transition-colors duration-300 flex items-center gap-1 py-2 text-gray-800"
            style={{ fontFamily: "var(--font-label)", fontWeight: 600 }}
          >
            {link.name}
            <span className="absolute bottom-0 left-0 h-0.5 bg-accent-teal transition-all duration-150 ease-out w-0 group-hover:w-full" />
          </a>
        )}

        {/* Dropdown Menu */}
        {hasDropdown && (
          <div
            className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
              isActive
                ? "opacity-100 visible translate-y-0"
                : "opacity-0 invisible -translate-y-2"
            }`}
          >
            <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-4 px-2 min-w-[280px]">
              <div className="grid grid-cols-1 gap-1">
                {link.dropdownItems.map((item) =>
                  item.isRoute ? (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-accent-teal hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      key={item.name}
                      href={item.href}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-accent-teal hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {item.name}
                    </a>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-md py-2 md:py-3 min-h-[56px] md:min-h-[68px]">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2"
              onClick={(e) => {
                // Eğer zaten ana sayfadaysak, hero bölümüne scroll yap
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <img
                src="/logo/hirethink-logo.svg"
                alt="HireThink"
                className="h-[40px] sm:h-[46px] md:h-[56px] w-auto"
                width="200"
                height="56"
                loading="eager"
                decoding="async"
              />
            </Link>

            {/* Desktop Navigation */}
            <div
              key={audience}
              className="hidden lg:flex items-center gap-8 animate-soft-swap"
            >
              {/* Product Links */}
              {productLinks.map((link) => (
                <NavLink key={link.name} link={link} />
              ))}

              {/* Divider */}
              <div className="w-px h-5 bg-gray-300" />

              {/* Contact */}
              <Link
                to={getRoute("contact", currentLanguage)}
                className="group relative text-sm font-semibold transition-colors duration-300 py-2 text-gray-600 hover:text-gray-900"
                style={{ fontWeight: 600 }}
              >
                {t("nav.contact")}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent-teal transition-all duration-150 ease-out group-hover:w-full" />
              </Link>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <LanguageSwitcher isScrolled={true} />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 transition-colors text-gray-700 touch-manipulation"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-primary z-[100] transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-4 sm:p-6 h-full overflow-y-auto">
          <div className="flex items-center justify-between mb-8 sm:mb-12">
            <Link
              to="/"
              onClick={(e) => {
                setIsMobileMenuOpen(false);
                // Eğer zaten ana sayfadaysak, hero bölümüne scroll yap
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <img
                src="/logo/hirethink-logo.svg"
                alt="HireThink"
                className="h-[40px] sm:h-[46px] w-auto"
                width="143"
                height="40"
                loading="eager"
                decoding="async"
              />
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <LanguageSwitcher isScrolled={false} />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-white touch-manipulation"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:gap-6">
            <div
              key={audience}
              className="space-y-4 sm:space-y-6 animate-soft-swap"
            >
              {productLinks.map((link) => (
                <NavLink key={link.name} link={link} isMobile />
              ))}
            </div>

            {/* Contact */}
            <Link
              to={getRoute("contact", currentLanguage)}
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-lg sm:text-xl font-semibold text-white hover:text-white transition-colors touch-manipulation py-2"
              style={{ fontWeight: 600 }}
            >
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
